import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Check, Copy, RefreshCw, X } from "lucide-react";

import { BrandLogo } from "@/components/brand/BrandLogo";
import { Button } from "@/components/ui/button";
import { runStudioRequest } from "@/lib/studio-request";
import {
  checkSession,
  decide,
  draftClientUpdate,
  listConnections,
  listRecommendations,
  runAnalysis,
  signIn,
  type ConnectionRow,
  type StoredRecommendation,
} from "@/lib/copilot.functions";

/**
 * The co-pilot dashboard. Internal — this is Adkiro's tool, not a client's.
 *
 * Unlisted on purpose: no nav link anywhere on the site, `noindex` in the
 * head, and every server call behind it refuses without an admin session.
 */

export const Route = createFileRoute("/studio")({
  head: () => ({
    meta: [{ title: "Studio | Adkiro" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: Studio,
});

interface RunSummary {
  summary: string;
  structuralNote: string | null;
  failed: boolean;
}

const SEVERITY_STYLE: Record<string, { label: string; className: string }> = {
  critical: { label: "Critical", className: "bg-[var(--diag-up)] text-white" },
  attention: {
    label: "Attention",
    className: "border border-[var(--diag-up)] text-[var(--diag-up)]",
  },
  watch: { label: "Watch", className: "border border-border text-muted-foreground" },
  good: {
    label: "Improved",
    className: "border border-[var(--diag-down)] text-[var(--diag-down)]",
  },
};

function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText(text).then(
          () => {
            setDone(true);
            setTimeout(() => setDone(false), 1500);
          },
          () => undefined,
        );
      }}
      className="inline-flex items-center gap-1.5 border border-border px-2 py-1 text-xs text-muted-foreground hover:border-primary hover:text-foreground"
    >
      {done ? <Check className="size-3" /> : <Copy className="size-3" />}
      {done ? "Copied" : "Copy steps"}
    </button>
  );
}

function SignIn({ onDone, configured }: { onDone: () => void; configured: boolean }) {
  const [key, setKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-5">
      <BrandLogo />
      <h1 className="mt-8 text-2xl">Studio</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Internal. Client ad-account data sits behind this.
      </p>

      {!configured && (
        <p className="mt-6 border border-primary/40 bg-card p-3 text-sm">
          <AlertTriangle className="mr-2 inline size-4 text-primary" />
          <code className="text-xs">ADMIN_ACCESS_KEY</code> is not set on the server yet, so no
          passphrase will work. Add it to <code className="text-xs">/opt/adkiro/.env</code> and
          recreate the container.
        </p>
      )}

      <form
        className="mt-6 flex flex-col gap-3"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          await runStudioRequest(() => signIn({ data: { key } }), {
            onSuccess: (res) => {
              if (res.ok) onDone();
              else setError(res.error ?? "Sign-in failed.");
            },
            onError: () => setError("Could not sign in. Please try again."),
            onSettled: () => setBusy(false),
          });
        }}
      >
        <input
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="Passphrase"
          autoComplete="current-password"
          maxLength={200}
          className="border border-border bg-background px-3 py-2 focus:border-primary focus:outline-none"
        />
        <Button type="submit" variant="brand" disabled={busy || !key}>
          {busy ? "Checking…" : "Enter"}
        </Button>
        {error && <p className="text-sm text-primary">{error}</p>}
      </form>
    </div>
  );
}

function RecommendationCard({
  rec,
  accountLabel,
  onDecided,
}: {
  rec: StoredRecommendation;
  accountLabel: string;
  onDecided: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const style = SEVERITY_STYLE[rec.severity] ?? SEVERITY_STYLE["watch"]!;
  const pending = rec.status === "pending";

  async function act(decision: "approved" | "dismissed") {
    setBusy(true);
    await decide({ data: { id: rec.id, decision } });
    setBusy(false);
    onDecided();
  }

  return (
    <article className={`border border-border p-5 ${pending ? "bg-background" : "bg-card/40"}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.12em] ${style.className}`}
            >
              {style.label}
            </span>
            <span className="text-sm font-medium">{rec.adSetName}</span>
            {rec.campaignName && (
              <span className="text-xs text-muted-foreground">· {rec.campaignName}</span>
            )}
            <span className="border border-border px-1.5 py-0.5 text-[0.6rem] uppercase tracking-[0.1em] text-muted-foreground">
              {accountLabel}
            </span>
          </div>
          <h3 className="mt-2 max-w-2xl leading-snug">{rec.headline}</h3>
        </div>
        {!pending && (
          <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
            {rec.status}
          </span>
        )}
      </div>

      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">{rec.detail}</p>

      {rec.caveats.length > 0 && (
        <ul className="mt-4 border-l-2 border-primary pl-3">
          {rec.caveats.map((c) => (
            <li key={c} className="text-xs leading-relaxed text-muted-foreground">
              {c}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <div>
          <p className="text-[0.6rem] uppercase tracking-[0.14em] text-primary">Judgement</p>
          <ol className="mt-2 flex flex-col gap-2">
            {rec.actions.map((a) => (
              <li key={a} className="text-sm leading-relaxed">
                {a}
              </li>
            ))}
          </ol>
        </div>
        <div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-[0.6rem] uppercase tracking-[0.14em] text-primary">
              Steps in Ads Manager
            </p>
            <CopyButton text={rec.manualSteps.map((s, i) => `${i + 1}. ${s}`).join("\n")} />
          </div>
          <ol className="mt-2 flex flex-col gap-2">
            {rec.manualSteps.map((s, i) => (
              <li key={s} className="grid grid-cols-[auto_1fr] gap-2 text-sm leading-relaxed">
                <span className="tabular-nums text-muted-foreground">{i + 1}</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {pending && (
        <div className="mt-5 flex flex-wrap gap-3 border-t border-border pt-4">
          <Button size="sm" variant="brand" disabled={busy} onClick={() => act("approved")}>
            <Check className="size-4" /> Approve
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => act("dismissed")}>
            <X className="size-4" /> Dismiss
          </Button>
          <span className="self-center text-xs text-muted-foreground">
            Approving records your decision. It does not change the account — you execute the steps
            above.
          </span>
        </div>
      )}
    </article>
  );
}

function Dashboard() {
  const [connections, setConnections] = useState<ConnectionRow[] | null>(null);
  const [active, setActive] = useState<string | null>(null);
  const [recs, setRecs] = useState<StoredRecommendation[]>([]);
  // Keyed by ad account. A single shared status meant analysing a second
  // account silently wiped the first one's summary and its structural note,
  // while that account's findings stayed in the list below — the numbers on
  // screen then described different accounts at the same time.
  const [runs, setRuns] = useState<Record<string, RunSummary>>({});
  const [accountFilter, setAccountFilter] = useState<string | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadConnections = useCallback(async () => {
    try {
      const res = await listConnections();
      if (res.ok) {
        setConnections(res.connections);
        setLoadError(null);
      } else {
        setConnections([]);
        setLoadError("Not authorised. Sign in again.");
      }
    } catch (cause) {
      // Without this the page sits on "Loading…" forever and says nothing
      // about why — the database being unreachable looks identical to a slow
      // request.
      console.error("[studio] listConnections failed:", cause);
      setConnections([]);
      setLoadError("Could not reach the database. Check the server is up and DATABASE_URL is set.");
    }
  }, []);

  const loadRecs = useCallback(async (connectionId: string) => {
    try {
      const res = await listRecommendations({ data: { connectionId } });
      setRecs(res.ok ? res.recommendations : []);
    } catch (cause) {
      console.error("[studio] listRecommendations failed:", cause);
      setRecs([]);
      setLoadError("Could not load recommendations.");
    }
  }, []);

  useEffect(() => {
    void loadConnections();
  }, [loadConnections]);

  // With one connection there is nothing to choose between, and making someone
  // click a list of one to reach the thing they came for is just a step.
  useEffect(() => {
    if (connections?.length === 1 && !active) setActive(connections[0]!.id);
  }, [connections, active]);

  useEffect(() => {
    if (active) void loadRecs(active);
  }, [active, loadRecs]);

  const connection = connections?.find((c) => c.id === active) ?? null;

  const accountName = (id: string) => connection?.adAccounts.find((a) => a.id === id)?.name ?? id;

  // Which accounts actually have findings, so the filter only offers real ones.
  const accountsWithFindings = [...new Set(recs.map((r) => r.adAccountId))];
  const visibleRecs = accountFilter ? recs.filter((r) => r.adAccountId === accountFilter) : recs;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-4">
            <BrandLogo compact />
            <span className="text-[0.65rem] uppercase tracking-[0.16em] text-muted-foreground">
              Studio · internal
            </span>
          </div>
          <button
            onClick={() => void loadConnections()}
            className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className="size-3.5" /> Refresh
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10">
        {loadError && (
          <p className="mb-6 flex items-start gap-2 border border-primary/40 bg-card p-3 text-sm">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-primary" />
            <span>{loadError}</span>
          </p>
        )}

        {connections === null && !loadError && <p className="text-muted-foreground">Loading…</p>}

        {connections?.length === 0 && !loadError && (
          <div className="border border-border bg-card/40 p-8">
            <h2 className="text-xl">No connected accounts yet</h2>
            <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">
              A client connects through the Meta consent flow, which needs the app created at{" "}
              <code className="text-xs">developers.facebook.com/apps</code> and{" "}
              <code className="text-xs">META_APP_ID</code> /{" "}
              <code className="text-xs">META_APP_SECRET</code> set on the server. Until then this is
              empty — the analysis has nothing to read.
            </p>
          </div>
        )}

        {connections && connections.length > 0 && (
          <>
            <div className="flex flex-wrap gap-2">
              {connections.map((c) => (
                <button
                  key={c.id}
                  onClick={() => {
                    // Switching connection clears everything scoped to the old
                    // one: its run summaries, its filter, its draft.
                    setActive(c.id);
                    setRuns({});
                    setAccountFilter(null);
                    setDraft(null);
                  }}
                  className={`border px-3 py-2 text-sm ${
                    active === c.id
                      ? "border-primary text-foreground"
                      : "border-border text-muted-foreground"
                  }`}
                >
                  {c.metaUserName ?? "Connection"}
                  {c.pendingCount > 0 && (
                    <span className="ml-2 tabular-nums text-xs text-primary">{c.pendingCount}</span>
                  )}
                </button>
              ))}
            </div>

            {connection && (
              <section className="mt-8">
                <div className="flex flex-wrap items-center gap-3 border-b border-border pb-5">
                  {connection.adAccounts.map((acct) => (
                    <Button
                      key={acct.id}
                      size="sm"
                      variant={acct.account_status === 1 ? "brand" : "outline"}
                      title={
                        acct.account_status === 1
                          ? `${acct.id}${acct.currency ? ` · ${acct.currency}` : ""}`
                          : `${acct.id} — Meta reports this account as not active (status ${acct.account_status})`
                      }
                      disabled={busy}
                      onClick={async () => {
                        setBusy(true);
                        setAccountFilter(acct.id);
                        const res = await runAnalysis({
                          data: { connectionId: connection.id, adAccountId: acct.id },
                        });
                        setBusy(false);
                        setRuns((prev) => ({
                          ...prev,
                          [acct.id]: res.ok
                            ? {
                                summary: `${res.examined} ad sets examined, ${res.skipped} skipped for missing data, ${res.found} findings.`,
                                structuralNote: res.structuralNote ?? null,
                                failed: false,
                              }
                            : {
                                summary: res.error ?? "Analysis failed.",
                                structuralNote: null,
                                failed: true,
                              },
                        }));
                        if (res.ok) {
                          await loadRecs(connection.id);
                          await loadConnections();
                        }
                      }}
                    >
                      Analyse {acct.name ?? acct.id}
                      {acct.account_status !== 1 && (
                        <span className="ml-1 text-[0.65rem] opacity-70">· inactive</span>
                      )}
                    </Button>
                  ))}

                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      setDraft(null);
                      const res = await draftClientUpdate({
                        data: { connectionId: connection.id },
                      });
                      setBusy(false);
                      setDraft(res.ok ? res.draft : (res.error ?? "Could not draft."));
                    }}
                  >
                    Draft client update
                  </Button>
                </div>

                {busy && <p className="mt-4 text-sm text-muted-foreground">Reading the account…</p>}

                {accountFilter && runs[accountFilter] && (
                  <div className="mt-4">
                    <p
                      className={`text-sm ${
                        runs[accountFilter]!.failed ? "text-primary" : "text-muted-foreground"
                      }`}
                    >
                      <span className="font-medium">{accountName(accountFilter)}</span>
                      {" — "}
                      {runs[accountFilter]!.summary}
                    </p>

                    {runs[accountFilter]!.structuralNote && (
                      <div className="mt-5 border-l-2 border-primary bg-card/40 p-5">
                        <p className="text-[0.6rem] uppercase tracking-[0.14em] text-primary">
                          Read this before the list
                        </p>
                        <p className="mt-2 max-w-3xl leading-relaxed">
                          {runs[accountFilter]!.structuralNote}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {draft && (
                  <div className="mt-5 border border-border p-5">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-[0.6rem] uppercase tracking-[0.14em] text-primary">
                        Draft client update — read it before you send it
                      </p>
                      <CopyButton text={draft} />
                    </div>
                    <p className="mt-3 max-w-3xl whitespace-pre-wrap leading-relaxed">{draft}</p>
                  </div>
                )}

                {accountsWithFindings.length > 1 && (
                  <div className="mt-8 flex flex-wrap items-center gap-2">
                    <span className="text-[0.6rem] uppercase tracking-[0.14em] text-muted-foreground">
                      Showing
                    </span>
                    <button
                      onClick={() => setAccountFilter(null)}
                      className={`border px-2.5 py-1 text-xs ${
                        accountFilter === null
                          ? "border-primary text-foreground"
                          : "border-border text-muted-foreground hover:border-primary/40"
                      }`}
                    >
                      All {recs.length}
                    </button>
                    {accountsWithFindings.map((id) => (
                      <button
                        key={id}
                        onClick={() => setAccountFilter(id)}
                        className={`border px-2.5 py-1 text-xs ${
                          accountFilter === id
                            ? "border-primary text-foreground"
                            : "border-border text-muted-foreground hover:border-primary/40"
                        }`}
                      >
                        {accountName(id)} {recs.filter((r) => r.adAccountId === id).length}
                      </button>
                    ))}
                  </div>
                )}

                <div className="mt-8 flex flex-col gap-5">
                  {recs.length === 0 && (
                    <p className="text-muted-foreground">
                      Nothing recorded yet. Run the analysis on an account above.
                    </p>
                  )}
                  {recs.length > 0 && visibleRecs.length === 0 && (
                    <p className="text-muted-foreground">
                      No findings for {accountName(accountFilter ?? "")}. The other accounts still
                      have some — use “All” above.
                    </p>
                  )}
                  {visibleRecs.map((rec) => (
                    <RecommendationCard
                      key={rec.id}
                      rec={rec}
                      accountLabel={accountName(rec.adAccountId)}
                      onDecided={() => {
                        void loadRecs(connection.id);
                        void loadConnections();
                      }}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function Studio() {
  const [state, setState] = useState<{ authorised: boolean; configured: boolean } | null>(null);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setChecking(true);
    setError(null);
    await runStudioRequest(() => checkSession(), {
      onSuccess: setState,
      onError: () => {
        setState(null);
        setError("Could not check access. Please try again.");
      },
      onSettled: () => setChecking(false),
    });
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!state) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-5">
        <BrandLogo />
        <h1 className="mt-8 text-2xl">Studio</h1>
        {error ? (
          <>
            <p role="alert" className="mt-6 text-sm text-primary">
              {error}
            </p>
            <Button
              className="mt-3"
              variant="brand"
              disabled={checking}
              onClick={() => void refresh()}
            >
              Try again
            </Button>
          </>
        ) : (
          <p role="status" className="mt-6 text-sm text-muted-foreground">
            Checking access…
          </p>
        )}
      </div>
    );
  }
  if (!state.authorised) return <SignIn onDone={refresh} configured={state.configured} />;
  return <Dashboard />;
}
